import { stripMarkdown } from './stripMarkdown'

/** `42s`, `6m 32s`, `1h 04m` — a call's length the way a phone log reads it. */
export const formatCallDuration = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, '0')}s`
  return `${seconds}s`
}

/** `0:42`, `6:32`, `1:04:09` — the running clock on a live call. */
export const formatCallClock = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${minutes}:${seconds}`
}

/** Arc's voice replies put what's read aloud above this line and the on-screen detail below it. */
export const DETAIL_DIVIDER = /^\s*---\s*$/m

const MAX_SPOKEN_CHARS = 600
/** Room for an email or note read out in full: a few minutes of speech. */
const MAX_DRAFT_CHARS = 4000

const QUOTE_MARKER = /^\s{0,3}>\s?/
/** A `.`, `?` or `!` (and any closing quote or bracket) followed by a space or a line break. */
const SENTENCE_END = /[.?!]["'”’)\]]*(?=\s)/g

/** Where the last full sentence that fits in `max` characters ends, or -1. */
const lastSentenceEnd = (text: string, max: number): number => {
  const matches = Array.from(text.slice(0, max + 1).matchAll(SENTENCE_END))
  const last = matches[matches.length - 1]
  return last ? (last.index ?? 0) + last[0].length : -1
}

/** Cut at the last sentence end (else line break) that fits, never mid-sentence. */
const capAtSentence = (text: string, max: number): string => {
  if (text.length <= max) return text
  const end = lastSentenceEnd(text, max)
  if (end > 0) return text.slice(0, end)
  const head = text.slice(0, max)
  const lastBreak = head.lastIndexOf('\n')
  return lastBreak > 0 ? head.slice(0, lastBreak).trimEnd() : `${head.trimEnd()}…`
}

interface IDraft {
  draft: string | null
  /** The draft ran past what can be read aloud, so `draft` is only its opening. */
  isDraftCut: boolean
}

/** A block-quoted draft as plain text, its paragraph breaks kept so it reads out as written. */
const draftText = (quoteLines: string[]): IDraft => {
  const body = quoteLines.map((line) => line.replace(QUOTE_MARKER, '')).join('\n')
  const draft = stripMarkdown(body)
    .replace(/[ \t]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  if (!draft) return { draft: null, isDraftCut: false }
  const capped = capAtSentence(draft, MAX_DRAFT_CHARS)
  return { draft: capped, isDraftCut: capped !== draft }
}

interface IReplySplit {
  spoken: string
  quoteLines: string[]
}

/** The block quote that starts right after the opening paragraph, blank lines inside it kept. */
const leadingQuoteBlock = (lines: string[]): string[] => {
  const block: string[] = []
  for (const line of lines) {
    if (QUOTE_MARKER.test(line)) block.push(line)
    else if (line.trim()) break
    else if (block.length > 0) block.push('')
  }
  return block
}

/** With no divider only the first paragraph is spoken, so only a quote right after it counts. */
const undividedSplit = (reply: string): IReplySplit => {
  const lines = reply.trim().split('\n')
  const paragraphEnd = lines.findIndex((line) => !line.trim() || QUOTE_MARKER.test(line))
  const split = paragraphEnd === -1 ? lines.length : paragraphEnd
  return {
    spoken: lines.slice(0, split).join('\n'),
    quoteLines: leadingQuoteBlock(lines.slice(split)),
  }
}

/** Above the divider: the quoted lines are the draft and the rest is spoken. */
const dividedSplit = (reply: string): IReplySplit => {
  const lines = reply.split(DETAIL_DIVIDER)[0].split('\n')
  return {
    spoken: lines.filter((line) => !QUOTE_MARKER.test(line)).join('\n'),
    quoteLines: lines.filter((line) => QUOTE_MARKER.test(line)),
  }
}

export interface IVoiceReplyParts extends IDraft {
  /** The opening to say, as plain capped text. */
  spoken: string
}

/**
 * What to say out loud from Arc's reply: everything above the divider, or the
 * first paragraph when Arc didn't use one, with a block-quoted draft pulled out
 * to be read in full.
 */
export const voiceReplyParts = (reply: string): IVoiceReplyParts => {
  const { spoken, quoteLines } = DETAIL_DIVIDER.test(reply)
    ? dividedSplit(reply)
    : undividedSplit(reply)
  return {
    spoken: capAtSentence(stripMarkdown(spoken).replace(/\s+/g, ' ').trim(), MAX_SPOKEN_CHARS),
    ...draftText(quoteLines),
  }
}
